import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  loadingContainerStyles,
  pickerCardStyles,
  clientNameClickableStyles,
} from "../../../styles/ClientPicker.styles";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import { StrategyGroupLabel, StrategyGroupEnum } from "constants/index";
import { Strategy, StrategyHistory, useGetStrategyHistoryQuery, useListStrategiesQuery } from "features/trading/accounts";
import { useState, useCallback, useMemo } from "react";

interface AssetPickerProps {
  type: "stock" | "strategy";
}

interface StockPickerCardProps {
  isOpen: boolean;
  isLoading: boolean;
  options: StrategyHistory[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (value: Strategy | null) => void;
}

const StockPickerCard = ({
  isOpen,
  isLoading,
  options,
  searchTerm,
  onSearchChange,
  onSelect,
}: StockPickerCardProps) => {
  if (!isOpen) return null;

  return (
    <Card sx={pickerCardStyles}>
      <CardContent>
        {isLoading ? (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            options={options}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option.strategy.name}
            loading={isLoading}
            onInputChange={(_, newValue) => onSearchChange(newValue)}
            onChange={(_, value) => onSelect(value?.strategy || null)}
            inputValue={searchTerm}
            open
            ListboxProps={{
              sx: {
                maxHeight: 170,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search stocks"
                variant="outlined"
                fullWidth
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.strategy.id}>
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <Typography>{option.strategy.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.strategy.strategy_group
                      ? StrategyGroupLabel[option.strategy_group]
                      : `Removed from the ${StrategyGroupLabel[option.strategy_group]} strategy`}
                  </Typography>
                </Box>
              </Box>
            )}
            sx={{ width: "100%" }}
            noOptionsText={
              searchTerm ? "No stocks found" : "Start typing to search stocks"
            }
            autoFocus
          />
        )}
      </CardContent>
    </Card>
  );
};

const AssetPicker = ({ type }: AssetPickerProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<Strategy | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const strategies = useMemo(() => {
    if (type === "stock") {
      return StrategyGroupEnum.STANDARD
    }
    else {
      return [StrategyGroupEnum.DIV_OPTIC, StrategyGroupEnum.GROWTH_OPTIC, StrategyGroupEnum.HEDGE]
    }
  }, [type]);

  const { data: strategyHistoryData, isLoading: isStrategyHistoryLoading } = useGetStrategyHistoryQuery({
      search: searchTerm.trim() || undefined,
      strategy_group: strategies,
      page_size: 100,
      page: 1,
  });

  const handleAssetClick = useCallback(() => {
    if (isPickerOpen) {
      handleClose();
    }
    else {
      setIsPickerOpen(true);
    }
  }, [isPickerOpen]);

  const handleClose = useCallback(() => {
    setIsPickerOpen(false);
    setSearchTerm("");
  }, []);

  const handleStockSelect = useCallback((value: Strategy | null) => {
    if (value) {
      setSelectedStock(value);
      setIsPickerOpen(false);
      setSearchTerm("");
    }
  }, []);


  const label = type === "stock" ? "Stock Symbol:" : "Strategy:";

  return (
    <Box>
      <Box sx={settingsReportStyles.summaryItem}>
        <Typography component="span" sx={settingsReportStyles.label}>
          {label}
        </Typography>
        <Typography
          component="span"
          sx={clientNameClickableStyles}
          onClick={handleAssetClick}
        >
          {selectedStock?.name || "[Stock Symbol]"}
        </Typography>
      </Box>

      <StockPickerCard
        isOpen={isPickerOpen}
        isLoading={isStrategyHistoryLoading}
        options={strategyHistoryData?.results || []}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSelect={handleStockSelect}
        />
    </Box>
  );
}

export default AssetPicker;
